import React from 'react';
import { UserPlus } from 'lucide-react';
import type { WorkspaceInvite } from '../../../types';
import InviteRow from './InviteRow';

interface Props {
  invites: WorkspaceInvite[];
  filter: 'open' | 'all';
  canManage: boolean;
  busyId: string | null;
  openInviteCount: number;
  inviteLimit: number;
  onFilterChange: (next: 'open' | 'all') => void | Promise<void>;
  onInviteClick: () => void;
  onResend: (invite: WorkspaceInvite) => void;
  onRevoke: (invite: WorkspaceInvite) => void;
}

const InvitesList: React.FC<Props> = ({
  invites,
  filter,
  canManage,
  busyId,
  openInviteCount,
  inviteLimit,
  onFilterChange,
  onInviteClick,
  onResend,
  onRevoke,
}) => {
  const atLimit = openInviteCount >= inviteLimit;

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-white">Invites</div>
          <div className="text-xs text-gray-400">
            {openInviteCount} open {openInviteCount === 1 ? 'invite' : 'invites'} (limit {inviteLimit})
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-white/5 rounded-lg p-0.5 text-xs">
            <button
              type="button"
              onClick={() => void onFilterChange('open')}
              className={`px-2.5 py-1 rounded ${filter === 'open' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:text-white'}`}
            >
              Open
            </button>
            <button
              type="button"
              onClick={() => void onFilterChange('all')}
              className={`px-2.5 py-1 rounded ${filter === 'all' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:text-white'}`}
            >
              All
            </button>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={onInviteClick}
              disabled={atLimit}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium rounded-lg"
              title={atLimit ? 'Invite limit reached — revoke an open invite first.' : undefined}
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Invite member</span>
            </button>
          )}
        </div>
      </div>

      {invites.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-400">
          {filter === 'open' ? 'No pending invites.' : 'No invites yet.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed min-w-[520px]">
            <colgroup>
              <col className="w-[38%]" />
              <col className="w-[14%]" />
              <col className="w-[18%]" />
              <col className="w-[18%]" />
              <col className="w-[12%]" />
            </colgroup>
            <thead className="bg-white/5 border-b border-white/10">
              <tr className="text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium hidden md:table-cell">Role</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium hidden lg:table-cell">Expires</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {invites.map((inv) => (
                <InviteRow
                  key={inv.id}
                  invite={inv}
                  canManage={canManage}
                  busy={busyId === inv.id}
                  onResend={onResend}
                  onRevoke={onRevoke}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvitesList;
